import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type {
  BillingPlanData,
  BillingPlanVersion,
} from "./billing-plan-values";
import {
  WAGE_PASS_THROUGH_OFF_HELP,
  WAGE_PASS_THROUGH_ON_HELP,
  WAGE_PASS_THROUGH_REPORTS_NOTE,
  WAGE_PASS_THROUGH_SWITCH_LABEL,
} from "./wage-pass-through-copy";

const STALE_VERSION_CODE = "CONFLICT";

/** Drafts keep their lines until discarded and regenerated with the new setting. */
function describeOpenDrafts(plan: BillingPlanVersion): string | null {
  if (plan.draftInvoiceCount === 0) {
    return null;
  }
  const drafts = `${plan.draftInvoiceCount} unissued draft${plan.draftInvoiceCount === 1 ? "" : "s"}`;
  return `This plan has ${drafts}. Issuing them will be refused until they are discarded and generated again from the Invoices panel.`;
}

export function PlanWagePassThroughDialog({
  plan,
  data,
  onSaved,
  onBusyChange,
  onReload,
  busy,
}: {
  plan: BillingPlanVersion;
  data: BillingPlanData;
  onSaved: () => Promise<void>;
  onBusyChange: (busy: boolean) => void;
  onReload: () => void;
  busy: boolean;
}) {
  const trpc = useTRPC();
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(plan.wagePassThroughEnabled);
  const openDrafts = describeOpenDrafts(plan);
  const mutation = useMutation(
    trpc.adminBillingPlans.updatePlanWagePassThrough.mutationOptions({
      onSuccess: onSaved,
    }),
  );
  const unchanged = enabled === plan.wagePassThroughEnabled;

  async function save() {
    if (mutation.isPending || unchanged) {
      return;
    }
    onBusyChange(true);
    try {
      await mutation.mutateAsync({
        organizationBusinessUnitId: data.unit.id,
        expectedVersion: data.version,
        planId: plan.id,
        wagePassThroughEnabled: enabled,
      });
      setOpen(false);
    } catch {
      // Stale submissions show the server's conflict and a reload action below.
    } finally {
      onBusyChange(false);
    }
  }

  return (
    <AlertDialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (mutation.isPending) {
          return;
        }
        if (nextOpen) {
          setEnabled(plan.wagePassThroughEnabled);
          mutation.reset();
        }
        setOpen(nextOpen);
      }}
    >
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy}>
          Edit
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Provider wages for this plan</AlertDialogTitle>
          <AlertDialogDescription>
            {WAGE_PASS_THROUGH_REPORTS_NOTE}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label
                htmlFor="plan-wage-pass-through-enabled"
                className="font-medium"
              >
                {WAGE_PASS_THROUGH_SWITCH_LABEL}
              </Label>
              <p className="text-xs text-muted-foreground">
                {enabled
                  ? WAGE_PASS_THROUGH_ON_HELP
                  : WAGE_PASS_THROUGH_OFF_HELP}
              </p>
            </div>
            <Switch
              id="plan-wage-pass-through-enabled"
              checked={enabled}
              disabled={mutation.isPending}
              onCheckedChange={(checked) => {
                mutation.reset();
                setEnabled(checked);
              }}
            />
          </div>
          {openDrafts && !unchanged && (
            <p
              role="alert"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            >
              {openDrafts}
            </p>
          )}
          {mutation.error && (
            <div className="space-y-2">
              <p role="alert" className="text-sm text-red-700">
                {mutation.error.message}
              </p>
              {mutation.error.data?.code === STALE_VERSION_CODE && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onReload}
                  disabled={mutation.isPending}
                >
                  Reload latest plan
                </Button>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <Button type="submit" disabled={mutation.isPending || unchanged}>
              {mutation.isPending ? "Saving…" : "Save provider wages"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
